import React from 'react'
import VerticalGroup from '../../../atoms/verticalgroup/VerticalGroup'
import TableWithSections, { SectionData } from '../tables/TableWithSections'
import { startCase } from '../../../../services/utils'
import { formatLendingCriteria, formatLossMultiplier, formatValue, percentage } from '../utils/formatters'

interface Props {
  configuration: ModelAPI.ConfigurationResponse
  data: ModelAPI.TeamResultsResponse['productBalances']
}

interface ProductTypes {
  name: string
  types: Array<{
    header?: string
    type?: string
    fields?: string[]
    totalSectionFields?: string[]
    fieldFormatter?: (field: string) => string | null
    formatter?: (val: number, field: string) => string | null
    sections?: Array<{
      name?: string
      isTotalSection?: boolean
      fields: string[]
      totalSectionFields?: string[]
      formatter?: (val: number, field: string) => string | null
      fieldFormatter?: (field: string) => string | null
    }>
  }>
}

const lieFormatter = (val: number, field: string) => {
  if (field === 'lie.lossMultiplier') { return formatLossMultiplier(val)}
  if (field === 'lie.lieRates') { return percentage(val) }
  return null
}

const lieSection = {
  name: 'Loan Impairment Expense',
  fields: ['lie.lossMultiplier', 'lie.lieRates', 'lie.lieLendingLosses'],
  formatter: lieFormatter
}

const projectImpactSections = {
  name: 'Project Impacts',
  fields: [
    'projectImpacts.project-offshore',
    'projectImpacts.project-right-size',
    'projectImpacts.project-harvest',
    'projectImpacts.project-mcfinsey',
    'projectImpacts.project-switch',
    'projectImpacts.project-lightspeed',
    'projectImpacts.project-digital',
    'projectImpacts.project-genius',
    'projectImpacts.project-nitro',
    'projectImpacts.project-big-data',
    'projectImpacts.project-care-bear',
    'projectImpacts.project-tighten',
    'projectImpacts.project-fix-it',
    'projectImpacts.project-roadshow',
    'projectImpacts.project-sustain',
    'projectImpacts.project-shield',
    'projectImpacts.project-simple',
    'projectImpacts.total'
  ],
  totalSectionFields: ['projectImpacts.total'],
  formatter: (val: number) => percentage(val)
}

const fields = ['openingBalance', 'annualGrowth', 'attrition']
const totalSectionFields = ['closingBalance', 'averageBalance']
const types: ProductTypes[] = [
  {
    name: 'HOME_LOANS',
    types: [
      {
        type: 'propietaryHomeLoans',
        sections: [
          { fields: fields.concat('lieAndNetWriteOffs').concat('loanBookSale') },
          { isTotalSection: true, fields: totalSectionFields },
          projectImpactSections,
          lieSection,
        ]
      },
      {
        type: 'brokerHomeLoans',
        sections: [
          { fields: fields.concat('lieAndNetWriteOffs') },
          { isTotalSection: true, fields: totalSectionFields },
          projectImpactSections,
          lieSection
        ]
      },
      { type: 'total', fields: fields.concat('lieAndNetWriteOffs').concat('loanBookSale'), totalSectionFields },
      { type: 'brokerCommissions', fields: ['averageBalance', 'averageAnnualBrokerCommission'], totalSectionFields: ['brokerCommissions'] },
      {
        type: 'loanBookPremium',
        fields: [
          'openingBalance',
          'premiumAcquired',
          'amortisation'
        ],
        totalSectionFields: ['closingBalance', 'averageBalance']
      }
    ]
  },
  {
    name: 'BUSINESS_LOANS',
    types: [
      {
        type: 'total',
        fields: [
          'openingBalance',
          'annualGrowth',
          'annualGrowthSecondary',
          'attrition',
          'lieAndNetWriteOffs'
        ],
        totalSectionFields,
        fieldFormatter: (field: string) => {
          if (field === 'annualGrowthSecondary') {
            return 'Unethical Loans Written'
          }
          return null
        }
      },
      {
        header: 'New Business Mix',
        sections: [{
          fields: ['lendingCriteria'],
          formatter: formatLendingCriteria
        }, {
          fields: ['newBusinessMix.goodQuality', 'newBusinessMix.mediumQuality', 'newBusinessMix.poorQuality'],
          formatter: (val: number) => percentage(val, 0)
        }]
      },
      {
        header: 'Loan Book - Quality Mix',
        sections: [{
          name: 'Good Quality',
          fields: ['loanBookQualityMix.goodQuality.openingBalance', 'loanBookQualityMix.goodQuality.annualGrowth', 'loanBookQualityMix.goodQuality.attrition', 'loanBookQualityMix.goodQuality.lieAndNetWriteOffs', 'loanBookQualityMix.goodQuality.closingBalance'],
          totalSectionFields: ['loanBookQualityMix.goodQuality.closingBalance'],
          formatter: (val: number, field: string) => ['loanBookQualityMix.goodQuality.attrition', 'loanBookQualityMix.goodQuality.lieAndNetWriteOffs'].includes(field) ? formatValue(-1 * val) : formatValue(val),
          fieldFormatter: (field: string) => field === 'loanBookQualityMix.goodQuality.lieAndNetWriteOffs' ? 'LIE / Gross Write Offs' : null
        }, {
          name: 'Unethical Loans',
          fields: ['loanBookQualityMix.unethicalLoans.openingBalance', 'loanBookQualityMix.unethicalLoans.closingBalance', 'loanBookQualityMix.unethicalLoans.averageBalance']
        }, {
          name: 'Medium Quality',
          fields: ['loanBookQualityMix.mediumQuality.openingBalance', 'loanBookQualityMix.mediumQuality.annualGrowth', 'loanBookQualityMix.mediumQuality.attrition', 'loanBookQualityMix.mediumQuality.lieAndNetWriteOffs', 'loanBookQualityMix.mediumQuality.closingBalance'],
          totalSectionFields: ['loanBookQualityMix.mediumQuality.closingBalance'],
          formatter: (val: number, field: string) => ['loanBookQualityMix.mediumQuality.attrition', 'loanBookQualityMix.mediumQuality.lieAndNetWriteOffs'].includes(field) ? formatValue(-1 * val) : formatValue(val),
          fieldFormatter: (field: string) => field === 'loanBookQualityMix.mediumQuality.lieAndNetWriteOffs' ? 'LIE / Gross Write Offs' : null
        }, {
          name: 'Poor Quality',
          fields: ['loanBookQualityMix.poorQuality.openingBalance', 'loanBookQualityMix.poorQuality.annualGrowth', 'loanBookQualityMix.poorQuality.attrition', 'loanBookQualityMix.poorQuality.lieAndNetWriteOffs', 'loanBookQualityMix.poorQuality.closingBalance'],
          totalSectionFields: ['loanBookQualityMix.poorQuality.closingBalance'],
          formatter: (val: number, field: string) => ['loanBookQualityMix.poorQuality.attrition', 'loanBookQualityMix.poorQuality.lieAndNetWriteOffs'].includes(field) ? formatValue(-1 * val) : formatValue(val),
          fieldFormatter: (field: string) => field === 'loanBookQualityMix.poorQuality.lieAndNetWriteOffs' ? 'LIE / Gross Write Offs' : null
        }, {
          fields: ['loanBookQualityMix.variance']
        }]
      },
      {
        header: 'Loan Impairment Expenses',
        type: 'lie',
        sections: [{
          name: 'Loss Multiplier',
          fields: ['lossMultiplier.goodQuality', 'lossMultiplier.mediumQuality', 'lossMultiplier.poorQuality'],
          formatter: formatLossMultiplier
        }, projectImpactSections, {
          name: 'LIE Rates',
          fields: ['lieRates.goodQuality', 'lieRates.mediumQuality', 'lieRates.poorQuality'],
          formatter: (val: number) => percentage(val)
        }, {
          name: 'LIE',
          fields: ['lie.goodQuality', 'lie.mediumQuality', 'lie.poorQuality', 'totalLie'],
          totalSectionFields: ['totalLie'],
        }]
      }
    ]
  },
  {
    name: 'CREDIT_CARDS',
    types: [
      { type: 'annualSpend', fields: fields.concat('lieAndNetWriteOffs').concat('excoDecisionImpacts'), totalSectionFields, fieldFormatter: (field: string) => field === 'lieAndNetWriteOffs' ? 'Spend lost due to losses' : null },
      { type: 'balances', fields: fields.concat('lieAndNetWriteOffs'), totalSectionFields },
      {
        type: 'interestEarningBalances',
        sections: [
          {
            fields: ['revolveRate', 'averageInterestEarningBalances'],
            formatter: (val: number, field: string) => field === 'revolveRate' ? percentage(val) : formatValue(val)
          },
          projectImpactSections,
          {
            name: 'Loan Impairment Expense',
            fields: ['lie.lossMultiplier', 'lie.lieRates', 'lie.lieLendingLosses', 'lie.spendLostDueToLosses'],
            formatter: lieFormatter
          }
        ]
      },
      { type: 'customers', fields: ['annualCustomerSpend', 'changeInAnnualCustomerSpend', 'averageCustomers', 'closingCustomers'] }
    ]
  },
  {
    name: 'totalAssets',
    types: [
      { fields: fields.concat('lieAndNetWriteOffs'), totalSectionFields: ['closingBalance'] },
    ]
  },
  {
    name: 'DEPOSITS',
    types: [
      { type: 'savingsAccounts', fields, totalSectionFields },
      { type: 'transactionAccounts', fields, totalSectionFields },
      { type: 'total', fields, totalSectionFields }
    ]
  },
  {
    name: 'wholesaleFunding',
    types: [
      {
        formatter: (val: number, field: string) => ['repayments',].includes(field) ? formatValue(-1 * val) : null,
        fields: ['openingBalance', 'annualGrowth', 'repayments'],
        totalSectionFields: ['closingBalance', 'averageBalance']
      },
    ]
  },
]

const pluckResultsByType = <K extends string, T extends Record<K, any>[]>(data: T, type: K, subType?: string) => {
  const results = []

  for (const year of data) {
    if (subType) {
      results.push(year[type][subType])
    } else {
      results.push(year[type])
    }
  }

  return results
}

const ModelTeamResultsProductBalances: React.FC<Props> = ({ data }) => (
  <VerticalGroup className='mt-2' spaceBetweenElements={2} wide>
    {types.map(type =>
      <VerticalGroup className='' key={type.name} spaceBetweenElements={2} wide>
        <h3 className='ml-4'>{startCase(type.name)}</h3>
        {type.types.map(subType => {
          const values = pluckResultsByType(data, type.name, subType.type)
          const tableData: SectionData[] = []
          if (subType.sections?.length) {
            for (const section of subType.sections) {
              tableData.push({
                name: section.name,
                isTotalsSection: section.isTotalSection ?? false,
                fields: section.fields,
                totalFields: section.totalSectionFields,
                values,
                formatter: section.formatter ?? subType.formatter,
                fieldFormatter: section.fieldFormatter ?? subType.fieldFormatter
              })
            }
          } else if (subType.fields?.length) {
            tableData.push({
              fields: subType.fields,
              values,
              formatter: subType.formatter,
              fieldFormatter: subType.fieldFormatter
            })

            if (subType.totalSectionFields) {
              tableData.push({
                isTotalsSection: true,
                fields: subType.totalSectionFields,
                values
              })
            }
          }

          return (<TableWithSections
            key={`${type.name} - ${subType.type}`}
            header={subType.header ?? startCase(subType.type ?? '')}
            data={tableData}
            fieldFormatter={(field) => field === 'lieAndNetWriteOffs' ? 'Lie / Net Write Offs' : null }
            formatter={(val, section, field) => ['attrition', 'lieAndNetWriteOffs'].includes(field) ? formatValue(-1 * val) : formatValue(val)}
          />)
        })}
      </VerticalGroup>
    )}
  </VerticalGroup>
)

export default ModelTeamResultsProductBalances